import React, { useState, useEffect } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as assetsApi from '../api/assets';

export function Assets() {
  const { selectedOrg } = useOrg();
  const { canReadAssets } = usePermissions();
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });

  useEffect(() => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    async function loadAssets() {
      setLoading(true);
      setError(null);
      try {
        const result = await assetsApi.getAssets(selectedOrg.id, {
          status: filters.status || undefined,
          search: filters.search || undefined
        });
        setAssets(result.assets);
        setTotal(result.total);
        setLastUpdated(result.lastUpdated);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    loadAssets();
  }, [selectedOrg, canReadAssets, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view assets.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader 
        title="Fleet Assets"
        subtitle={`${total} assets in ${selectedOrg?.name}`}
      />

      <div style={styles.toolbar}>
        <div style={styles.filters}>
          <input
            type="text"
            placeholder="Search assets..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            style={styles.searchInput}
          />
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            style={styles.select}
          >
            <option value="">All Statuses</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="maintenance">Maintenance</option>
          </select>
        </div>
        {lastUpdated && (
          <span style={styles.lastUpdated}>
            Last updated: {new Date(lastUpdated).toLocaleString()}
          </span>
        )}
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading assets...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Site</th>
                <th style={styles.th}>Model</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Program</th>
                <th style={styles.th}>Ports</th>
                <th style={styles.th}>Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {assets.map(asset => (
                <tr key={asset.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div>
                      <div style={styles.assetName}>{asset.name}</div>
                      <div style={styles.serialNumber}>{asset.serialNumber}</div>
                    </div>
                  </td>
                  <td style={styles.td}>{asset.siteName}</td>
                  <td style={styles.td}>{asset.model}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.statusBadge,
                      background: getStatusColor(asset.status).bg,
                      color: getStatusColor(asset.status).text
                    }}>
                      {asset.status}
                    </span>
                  </td>
                  <td style={styles.td}>{asset.program}</td>
                  <td style={styles.td}>{asset.ports?.length || 0}</td>
                  <td style={styles.td}>
                    {asset.lastSeen ? new Date(asset.lastSeen).toLocaleString() : '-'}
                  </td>
                </tr>
              ))}
              {assets.length === 0 && (
                <tr>
                  <td colSpan="7" style={styles.emptyState}>
                    No assets found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function getStatusColor(status) {
  const colors = {
    online: { bg: '#c6f6d5', text: '#276749' },
    offline: { bg: '#fed7d7', text: '#c53030' },
    maintenance: { bg: '#fefcbf', text: '#975a16' }
  };
  return colors[status] || { bg: '#e2e8f0', text: '#4a5568' };
}

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1400px',
    margin: '0 auto'
  },
  accessDenied: {
    padding: '3rem',
    textAlign: 'center',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem'
  },
  filters: {
    display: 'flex',
    gap: '0.5rem'
  },
  searchInput: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    width: '250px'
  },
  select: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    background: 'white'
  },
  lastUpdated: {
    fontSize: '0.75rem',
    color: '#718096'
  },
  error: {
    padding: '1rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  tableContainer: {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '1rem',
    background: '#f7fafc',
    borderBottom: '1px solid #e2e8f0',
    fontWeight: '600',
    fontSize: '0.875rem',
    color: '#4a5568'
  },
  tr: {
    borderBottom: '1px solid #e2e8f0'
  },
  td: {
    padding: '1rem',
    fontSize: '0.875rem'
  },
  assetName: {
    fontWeight: '500'
  },
  serialNumber: {
    fontSize: '0.75rem',
    color: '#718096'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '500',
    textTransform: 'capitalize'
  },
  emptyState: {
    padding: '3rem',
    textAlign: 'center',
    color: '#718096'
  }
};
